import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { createCompany } from "@/components/api/companiesApi";

const schema = z.object({
    name: z.string().trim().min(1, "Company name is required"),
});

type FormValues = z.infer<typeof schema>;

interface NewCompanyModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function NewCompanyModal({ open, onOpenChange }: NewCompanyModalProps) {
    const navigate = useNavigate();
    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { name: "" },
    });

    // Reset the field whenever the modal is (re)opened.
    useEffect(() => {
        if (open) form.reset({ name: "" });
    }, [open, form]);

    const onSubmit = async (values: FormValues) => {
        try {
            const company = await createCompany({ name: values.name });
            onOpenChange(false);
            navigate(`/cms/${company.id}`);
        } catch {
            // Error already surfaced via errorEvents by the API layer.
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>New company</DialogTitle>
                    <DialogDescription>
                        Just the name to start — you can fill in everything else next.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-2">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Company name</FormLabel>
                                    <FormControl>
                                        <Input placeholder="e.g. Acme Realty" autoFocus {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className="flex justify-end gap-3 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={form.formState.isSubmitting}>
                                {form.formState.isSubmitting ? "Creating…" : "Create company"}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
